import { type JSX } from 'react';
import { generatePath } from 'react-router';

import { CircleIndicator } from 'Components/Indicator/CircleIndicator';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { Table } from 'Components/Table/Table';
import { Text } from 'Components/Text/Text';
import { RISK_REVIEW_DETAILS } from 'Config/PathTemplates';
import { getEffectivenessVariantColor, numberAsEffectiveness } from 'Models/OperationalControls';
import { RiskAssessment, RiskHistory, formatAverageWeightedEffectiveness } from 'Models/RiskRegister';

import styles from './AssessmentHistoryTable.module.css';
import { AverageOrTotal } from '../AssessmentListing';

export interface AssessmentHistoryTableProps {
    riskAssessments: RiskAssessment[];
    averageOrTotal: AverageOrTotal;
}

// TODO: Either the squashed commit that includes this line should be reverted, and the backend should update its calculation logic; or the backend logic should be removed, and the frontend should always do these calculations (in a single place--this logic also exists in RiskCallout). Regardless, a data migration will be necessary, and the logic (wherever it lives) should be tested.
const calculateTotalTargetRisk = (risks: RiskHistory[]) => {
    return (risks.map((risk) => risk.total_target_risk ?? risk.total_current_risk).filter((value) => value !== undefined) as number[]).reduce((a, b) => a + b, 0);
};

const calculateAverageTargetRisk = (risks: RiskHistory[]) => {
    if (risks.length === 0) {
        return 0;
    }

    const total = calculateTotalTargetRisk(risks);
    return total / risks.length;
};

export const AssessmentHistoryTable = (props: AssessmentHistoryTableProps): JSX.Element => {
    const isAverage = props.averageOrTotal === AverageOrTotal.AVERAGE;

    const columns: ColumnConfig<RiskAssessment>[] = [
        {
            type: ColumnType.DATE,
            name: 'Date',
            getDateString: (assessment) => assessment.timestamp,
            getOnClickTextAction: (assessment) => ({
                goToLocation: generatePath(RISK_REVIEW_DETAILS, { timestamp: assessment.timestamp }),
            }),
        },
        {
            type: ColumnType.STRING,
            name: 'Category',
            getString: (assessment) => assessment.category.title,
        },
        {
            type: ColumnType.DECIMAL,
            name: 'Inherent Risk',
            getNumber: (assessment) => (isAverage ? assessment.average_inherent_risk : assessment.total_inherent_risk),
            numberOfDigitsAfterDecimalPoint: 2,
        },
        {
            type: ColumnType.CUSTOM,
            name: 'Control Environment',
            getValue: (assessment) => assessment.average_control_environment_effectiveness,
            getTableCellContent: (assessment) => (
                <div className={styles.controlEnvironmentEffectivenessContainer}>
                    <CircleIndicator variant={getEffectivenessVariantColor(numberAsEffectiveness(assessment.average_control_environment_effectiveness))} />
                    <Text noStyles>{formatAverageWeightedEffectiveness(assessment.average_control_environment_effectiveness)}</Text>
                </div>
            ),
            sortComparator: (a, b) => (a > b ? 1 : -1),
        },
        {
            type: ColumnType.DECIMAL,
            name: 'Current Residual Risk',
            getNumber: (assessment) => (isAverage ? assessment.average_current_risk : assessment.total_current_risk),
            numberOfDigitsAfterDecimalPoint: 2,
        },
        {
            type: ColumnType.DECIMAL,
            name: 'Target Residual Risk',
            getNumber: (assessment) => (isAverage ? calculateAverageTargetRisk(assessment.risk_histories) : calculateTotalTargetRisk(assessment.risk_histories)),
            numberOfDigitsAfterDecimalPoint: 2,
        },
    ];

    return <Table columns={columns} defaultSortColumn="Date" defaultSortDirection="descending" filters={[]} getUniqueIdForRow={(assessment) => assessment.timestamp} rowsData={props.riskAssessments} />;
};
